import React, { useEffect, useState, useRef } from "react";
import { Table, Spin, message, Empty, Input, Space, Button } from "antd";
import { Link } from "react-router-dom";
import update from "immutability-helper";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { API_BASE_URL } from "../api/api";
import * as XLSX from "xlsx";
import { ExportOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const type = "DragableBodyColumn";

const DragableHeaderCell = ({
  index,
  moveColumn,
  className,
  style,
  ...restProps
}) => {
  const ref = useRef();

  const [, drop] = useDrop({
    accept: type,
    hover(item) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      moveColumn(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [, drag] = useDrag({
    type,
    item: { index },
  });

  drag(drop(ref));

  return (
    <th
      ref={ref}
      className={className}
      style={{
        cursor: "move",
        userSelect: "none",
        ...style,
      }}
      {...restProps}
    />
  );
};

function PointsEarnedPage() {
  const [earnedPointsRecords, setEarnedPointsRecords] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    const fetchPoints = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        message.error("User not authenticated.");
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}pointsEarned`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          setEarnedPointsRecords(json.data);
        } else {
          message.error("Failed to fetch points data.");
        }
      } catch (error) {
        console.error(error);
        message.error("Error fetching points data.");
      } finally {
        setLoading(false);
      }
    };

    fetchPoints();
    setColumns(initColumns);
  }, []);

  const initColumns = [
    {
      title: "ID",
      dataIndex: "id",
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Member ID",
      dataIndex: "member_id",
      render: (member_id) => <Link to={`/user/${member_id}`}>{member_id}</Link>,
      sorter: (a, b) => a.member_id - b.member_id,
    },
    {
      title: "Order Number",
      dataIndex: "order_id",
      render: (order_id) => (
        <Link to={`/vacation-orders/${order_id}`}>{order_id}</Link>
      ),
      sorter: (a, b) => a.order_id - b.order_id,
    },
    {
      title: "Order Type",
      dataIndex: "order_type",
      sorter: (a, b) => a.order_type.localeCompare(b.order_type),
      render: (type) => {
        const styleMap = {
          vacation: {
            backgroundColor: "#e6f7ff",
            color: "#1890ff",
            border: "1px solid #91d5ff",
          },
          hotel: {
            backgroundColor: "#fffbe6",
            color: "#faad14",
            border: "1px solid #ffe58f",
          },
        };
        const style = {
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: "12px",
          fontWeight: 500,
          textTransform: "capitalize",
          fontSize: "12px",
          ...(styleMap[type] || {}),
        };
        return <span style={style}>{type}</span>;
      },
    },
    {
      title: "Points",
      dataIndex: "points",
      sorter: (a, b) => a.points - b.points,
    },
    {
      title: "Valid Date",
      dataIndex: "valid_date",
      sorter: (a, b) => (a.valid_date || "").localeCompare(b.valid_date || ""),
    },
    {
      title: "Credited?",
      dataIndex: "credited",
      render: (v) => (
        <span style={{ color: v ? "green" : "red", fontWeight: 500 }}>
          {v ? "Yes" : "Pending"}
        </span>
      ),
      renderExport: (credited) => (credited === 1 ? "Yes" : "Pending"),
      sorter: (a, b) => (a.credited ? 1 : 0) - (b.credited ? 1 : 0),
    },
    {
      title: "Created At",
      dataIndex: "create_at",
      sorter: (a, b) => (a.create_at || "").localeCompare(b.create_at || ""),
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      sorter: (a, b) => (a.updated_at || "").localeCompare(b.updated_at || ""),
      render: (value) =>
        value ? dayjs.utc(value).local().format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  const moveColumn = (dragIndex, hoverIndex) => {
    const dragColumn = columns[dragIndex];
    setColumns(
      update(columns, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragColumn],
        ],
      })
    );
  };

  const components = {
    header: {
      cell: (props) => (
        <DragableHeaderCell {...props} moveColumn={moveColumn} />
      ),
    },
  };

  const finalColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: () => ({
      index,
      moveColumn,
    }),
  }));

  const filteredEarnedPointsRecords = earnedPointsRecords.filter((item) => {
    const search = searchText.toLowerCase();
    return (
      item.member_id?.toString().toLowerCase().includes(search) ||
      item.order_id?.toString().toLowerCase().includes(search)
    );
  });

  const handleExport = () => {
    const exportData = filteredEarnedPointsRecords.map((row) => {
      const rowData = {};
      columns.forEach((col) => {
        const value = row[col.dataIndex];

        if (col.renderExport) {
          rowData[col.title] = col.renderExport(value, row);
        } else if (col.render) {
          const rendered = col.render(value, row);
          rowData[col.title] = typeof rendered === "string" ? rendered : value;
        } else {
          rowData[col.title] = value;
        }
      });
      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Points Earned");
    XLSX.writeFile(workbook, "points_earned.xlsx");
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", paddingTop: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!earnedPointsRecords.length) {
    return (
      <div style={{ textAlign: "center", paddingTop: 50 }}>
        <Empty description="No points earned data found" />
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div style={{ padding: 24 }}>
        <h2>Points Earned</h2>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Input
            placeholder="Search by Member ID or Order Number"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ marginBottom: 16, maxWidth: 300 }}
            allowClear
          />
          <Button
            type="primary"
            onClick={handleExport}
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              borderRadius: "8px",
              backgroundColor: "rgba(0, 128, 0, 0.1)", // Soft green background
              color: "#008000", // Strong green text color
              border: "1px solid #008000", // Green border
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              transition: "background-color 0.3s ease, transform 0.3s ease",
            }}
            icon={<ExportOutlined />}
            onMouseOver={(e) =>
              (e.target.style.backgroundColor = "rgba(0, 128, 0, 0.2)")
            } // Slightly darker green on hover
            onMouseOut={(e) =>
              (e.target.style.backgroundColor = "rgba(0, 128, 0, 0.1)")
            } // Revert to soft green
          >
            Export to Excel
          </Button>
        </div>
        <div
          style={{
            margin: "16px 0",
            padding: "8px 16px",
            backgroundColor: "#fafafa",
            border: "1px solid #d9d9d9",
            borderRadius: "8px",
            fontWeight: 500,
            fontSize: "14px",
            color: "#555",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
          }}
        >
          Showing{" "}
          <span style={{ color: "#1890ff", fontWeight: 600 }}>
            {filteredEarnedPointsRecords.length}
          </span>{" "}
          result{filteredEarnedPointsRecords.length !== 1 ? "s" : ""} found
        </div>
        <div className="compact-table-wrapper">
          <Table
            dataSource={filteredEarnedPointsRecords}
            columns={finalColumns}
            rowKey="id"
            components={components}
            bordered
            scroll={{ x: "max-content" }}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "25", "50", "100"],
            }}
          />
        </div>
      </div>
    </DndProvider>
  );
}

export default PointsEarnedPage;
